import { ErrorHandler, Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { DataStoreService, WINDOW } from '@kservice';
import { HttpStatusCode } from '@ktypes/enums';
import * as Sentry from '@sentry/angular';
import { BrowserTracing } from '@sentry/tracing';
import { DeviceDetectorService } from 'ngx-device-detector';
import { environment } from '../../environments/environment';

Sentry.init({
  dsn: 'https://297acee3f8b8402b8146eb2a6b79b0a9@sentry.io/4616741',
  environment: environment.environment,
  beforeSend(event) {
    // This is because Angular tests do not run through handleError
    const regex = RegExp(/localhost|127.0.0.1|0.0.0.0|10.0.*|192.168.*/g);
    if (environment.environment !== 'dev' && !regex.test(event.request.url)) {
      return event;
    }
  },
  integrations: [
    // Registers and configures the Tracing integration,
    // which automatically instruments your application to monitor its
    // performance, including custom Angular routing instrumentation
    new BrowserTracing({
      tracingOrigins: ['localhost', ...Object.values(environment.appUrls), environment.apiHostUrl],
      routingInstrumentation: Sentry.routingInstrumentation,
    }),
  ],
});

@Injectable({
  providedIn: 'root',
})
export class CustomErrorHandler implements ErrorHandler {
  constructor(
    private _dataStoreService: DataStoreService,
    private _deviceDetectorService: DeviceDetectorService,
    private _ngZone: NgZone,
    private _router: Router,
    @Inject(WINDOW) private _window: Window
  ) {}

  _console: Console = console;

  handleError(error) {
    const originalError = findOriginalError(error);
    const context = findContext(error);
    // Note: Browser consoles show the place from where console.warn was called.
    // We can use this to give users additional information about the error.
    const errorLogger = getErrorLogger(error);
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;

    // Check for error and send to Sentry if not DEV environment
    const browser = this._deviceDetectorService.getDeviceInfo().browser;
    const url = this._window.location.origin;
    const regex = RegExp(/localhost|127.0.0.1|0.0.0.0|10.0.*|192.168.*/g);

    // If this is a Chunk load error, reload the page
    if (chunkFailedMessage.test(error.message)) {
      this._window.location.reload();
    }

    // If not on DEV, send issue to Sentry
    if (environment.environment !== 'dev' && browser !== 'IE' && !regex.test(url)) {
      const groupCode = this._dataStoreService.authData?.user?.group?.groupCode;
      Sentry.withScope((scope) => {
        scope.setExtra('groupCode', groupCode);
        Sentry.captureException((error && error.originalError) || error || new Error('Error was null or undefined'));
      });
    }
    // if not sending to Sentry, log to the console as an error
    else {
      errorLogger(this._console, `ERROR`, error);
      if (originalError) {
        errorLogger(this._console, `ORIGINAL ERROR`, originalError);
      }
      if (context) {
        errorLogger(this._console, 'ERROR CONTEXT', context);
      }
    }

    if (
      error.status &&
      ![
        HttpStatusCode.BAD_REQUEST,
        HttpStatusCode.UNAUTHORIZED,
        HttpStatusCode.PAYMENT_REQUIRED,
        HttpStatusCode.FORBIDDEN,
        HttpStatusCode.NOT_FOUND,
      ].includes(error.status)
    ) {
      void this._ngZone.run(() => this._router.navigate(['error']));
    }
  }
}

export const ERROR_DEBUG_CONTEXT = 'ngDebugContext';
export const ERROR_ORIGINAL_ERROR = 'ngOriginalError';
export const ERROR_LOGGER = 'ngErrorLogger';

function findContext(error: any): any {
  if (error) {
    return getDebugContext(error) ? getDebugContext(error) : findContext(getOriginalError(error));
  }

  return null;
}

function findOriginalError(error: Error): any {
  let e = getOriginalError(error);
  while (e && getOriginalError(e)) {
    e = getOriginalError(e);
  }

  return e;
}

function getDebugContext(error: Error) {
  return (error as any)[ERROR_DEBUG_CONTEXT];
}

function getOriginalError(error: Error): Error {
  return (error as any)[ERROR_ORIGINAL_ERROR];
}

function getErrorLogger(error: Error): (console: Console, ...values: any[]) => void {
  return (error as any)[ERROR_LOGGER] || defaultErrorLogger;
}

function defaultErrorLogger(console: Console, ...values: any[]) {
  (console.error as any)(...values);
}
